// React
import { Dispatch, FunctionComponent, SetStateAction, useReducer } from "react";
// Component
import { Title, FhirStatus, StatusAlert, StatusIcon } from '@fyrstain/fhir-front-library';
// React Bootstrap
import { Accordion, Form, Table } from 'react-bootstrap';
// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faRotate, faTrash } from '@fortawesome/free-solid-svg-icons';
// Translation
import i18n from 'i18next';

////////////////////////////////////////
//             Interfaces             //
////////////////////////////////////////
interface AbstractServerSectionConfiguration {
    id: string,
    options: { code: string, display: string }[]
    requiredMessage?: string,
    servers: { id: number, profile: string, url: string }[],
    setServers: Dispatch<SetStateAction<{ id: number; profile: string; url: string; }[]>>,
    titleKey: string,
    addKey: string,
    deleteMessageKey: string,
    isServerProfileUsed: (id: string) => boolean
}

const AbstractServerSection: FunctionComponent<AbstractServerSectionConfiguration> = (configs) => {

    ////////////////////////////////////////
    //               States               //
    ////////////////////////////////////////

    const [counter, incrementCounter] = useReducer(x => x + 1, 0);

    ////////////////////////////////////////
    //              Constants             //
    ////////////////////////////////////////

    /**
     * Default fields for a new server line.
     */
    const newServer = {
        profile: '',
        url: ''
    }

    ////////////////////////////////////////
    //              Functions             //
    ////////////////////////////////////////

    /**
     * Handle the change of the input fields the abstract server
     * 
     * @param index 
     * @param field 
     * 
     */
    const handleChange = (index: number, field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const newServers = configs.servers.map((item, i) => {
            if (i === index) {
                return { ...item, [field]: e.target.value };
            }
            return item;
        });
        configs.setServers(newServers);
    };

    /**
     * Add a new server to the list
     * 
     */
    const addRow = () => {
        incrementCounter();
        const newItem = { id: `${configs.id}-${counter}`, ...newServer };
        configs.setServers(prevItems => [...prevItems, newItem] as { id: number, profile: string, url: string }[]);
    };

    /**
     * Delete a row, corresponding to a server
     * 
     * @param index 
     * 
     */
    const deleteRow = (index: number) => {
        // Check if the server profile is used in any operation
        if (configs.isServerProfileUsed(configs.servers[index].profile)) {
            window.alert(i18n.t('errormessage.serverprofileallreadyused'));
            return;
        }
        const confirmDelete = window.confirm(i18n.t(configs.deleteMessageKey));
        if (!confirmDelete) {
            return;
        }
        // Delete the element at the specified index
        const newItems = configs.servers.filter((_, i) => i !== index);
        // Update status based on type
        configs.setServers(newItems);
    };

    /** 
     * Refresh a server's information
     * 
     * @param index index of the server in the list.
     * 
     */
    const refreshRow = (index: number) => {
        const newServers = configs.servers.map((item, i) => {
            if (i === index) {
                return { ...item, ...newServer };
            }
            return item;
        });
        configs.setServers(newServers);
    };

    ////////////////////////////////////////
    //              Content               //
    ////////////////////////////////////////

    return <Accordion
        defaultActiveKey="0"
        alwaysOpen
    >
        <Accordion.Item eventKey="0">
            <Accordion.Header>
                <Title
                    level={2}
                    content={i18n.t(configs.titleKey)}
                />
            </Accordion.Header>
            <Accordion.Body>
                {(configs.servers.length === 0 && configs.requiredMessage) &&
                    <StatusAlert
                        status={FhirStatus.warning}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <StatusIcon status={FhirStatus.warning} />
                            <strong>
                                {i18n.t(configs.requiredMessage)}
                            </strong>
                        </div>
                    </StatusAlert>
                }
                {(configs.servers.length > 0) &&
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th className="col-5">
                                    {i18n.t('general.profile')} *
                                </th>
                                <th className="col-5">
                                    URL
                                </th>
                                <th>
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {configs.servers.map((server, index) => (
                                <tr key={index}>
                                    <td>
                                        <Form.Select
                                            required
                                            value={server.profile}
                                            onChange={handleChange(index, 'profile')}
                                        >
                                            {configs.options.map(option => (
                                                <option
                                                    key={option.code}
                                                    value={option.code}
                                                >
                                                    {option.display}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </td>
                                    <td>
                                        <Form.Control
                                            value={server.url}
                                            onChange={handleChange(index, 'url')}
                                        />
                                    </td>
                                    <td className="align-middle">
                                        <FontAwesomeIcon
                                            icon={faRotate}
                                            className="actionIcon"
                                            onClick={() => refreshRow(index)}
                                        />
                                        <FontAwesomeIcon
                                            icon={faTrash}
                                            className="actionIcon"
                                            onClick={() => deleteRow(index)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                }
                <div
                    className="actionIcon d-flex flex-wrap align-items-center gap-2 w-11rem"
                    onClick={() => addRow()}
                >
                    <FontAwesomeIcon
                        icon={faPlus}
                        role="button"
                    />
                    <strong
                        role="button"
                    >
                        {i18n.t(configs.addKey)}
                    </strong>
                </div>
            </Accordion.Body>
        </Accordion.Item>
    </Accordion>
};

export default AbstractServerSection;